/**
 * Schema 管理器
 * 负责 Schema 的安全更新、验证和导出
 */

import type {
  SchemaInput,
  ValidationResult,
  NumberingLevel,
  ParagraphStyle,
  AbstractNum,
  NumFormat,
  Alignment,
} from './types';
import {
  immutableSet,
  computeDiff,
  deepClone,
} from '../utils/immutable';

// ============ 常量 ============

/** UI 支持的最大级别 */
export const MAX_UI_LEVEL = 4;

/** 必需的级别数量 */
export const REQUIRED_LEVELS = 9;

/** 必需的级别字段 */
const REQUIRED_LEVEL_FIELDS = ['level', 'start', 'numFmt', 'indent'] as const;

// ============ 默认模板 ============

/** 默认级别模板 */
const DEFAULT_LEVEL_TEMPLATE: Omit<NumberingLevel, 'level' | 'style'> = {
  start: 1,
  numFmt: 'decimal' as NumFormat,
  pattern: '%1.',
  indent: {
    left: 440,
    hanging: 440,
  },
  continuous: false,
};

/** 默认样式模板 */
const DEFAULT_STYLE_TEMPLATE: Omit<ParagraphStyle, 'name'> = {
  basedOn: null,
  next: null,
  spacing: {
    before: '0',
    after: '240',  // 增加段落后间距，避免行距1x时过于紧凑
    line: '260',
    lineRule: 'auto',
  },
  font: {
    ascii: 'Calibri',
    eastAsia: '微软雅黑',
    hAnsi: 'Calibri',
  },
  fontSize: 21, // 10.5pt in half-points
  bold: false,
  italic: false,
  align: 'left',
  indent: undefined,
};

/** 生成级别样式名称 */
export function generateStyleName(abstractNumId: number, level: number): string {
  return `numbering-${abstractNumId}-lv${level}`;
}

/** 生成级别的默认配置（根据级别调整缩进和 pattern） */
function generateDefaultLevelConfig(level: number): Omit<NumberingLevel, 'level' | 'style'> {
  const baseIndent = 440; // 基础缩进
  const indentStep = 440; // 每级缩进增量

  return {
    ...DEFAULT_LEVEL_TEMPLATE,
    // pattern 使用 1-based 索引：Level 0 -> %1, Level 5 -> %6
    pattern: `%${level + 1}.`,
    indent: {
      left: baseIndent + level * indentStep,
      hanging: 440,
    },
  };
}

/** 生成级别的默认样式 */
function generateDefaultStyle(abstractNumId: number, level: number): ParagraphStyle {
  // 根据级别调整字号
  const fontSizeMap: Record<number, number> = {
    0: 32,  // 16pt - 一级标题
    1: 28,  // 14pt - 二级标题
    2: 24,  // 12pt - 三级标题
    3: 22,  // 11pt - 四级标题
    4: 21,  // 10.5pt - 五级标题
  };

  return {
    ...DEFAULT_STYLE_TEMPLATE,
    name: generateStyleName(abstractNumId, level),
    fontSize: fontSizeMap[level] ?? 21,
    bold: level <= 1, // 一、二级标题加粗
  };
}

// ============ 安全更新 ============

export interface UpdateOptions {
  /** 是否允许修改 Level 5-8，默认 false */
  allowHighLevels?: boolean;
  /** 是否进行验证，默认 true */
  validate?: boolean;
}

/**
 * 安全更新 Numbering Level 属性
 * @param schema - 原始 Schema
 * @param abstractNumId - AbstractNum ID
 * @param levelIndex - Level 索引 (0-8)
 * @param property - 属性路径
 * @param value - 新值
 * @param options - 更新选项
 * @returns 更新后的 Schema
 */
export function updateLevelProperty(
  schema: SchemaInput,
  abstractNumId: number,
  levelIndex: number,
  property: string[],
  value: unknown,
  options: UpdateOptions = {}
): SchemaInput {
  const { allowHighLevels = false, validate = true } = options;

  // 验证 Level 范围
  if (!allowHighLevels && levelIndex > MAX_UI_LEVEL) {
    throw new Error(
      `Cannot modify level ${levelIndex}. UI only supports Level 0-${MAX_UI_LEVEL}. ` +
      `Use allowHighLevels: true to override.`
    );
  }

  // 构建完整路径
  const fullPath = [
    'numberingSchemas',
    `AbstractNum_${abstractNumId}`,
    'levels',
    String(levelIndex),
    ...property,
  ];

  // 验证更新
  if (validate) {
    validateLevelUpdate(property, value);
  }

  return immutableSet(schema, fullPath, value);
}

/**
 * 安全更新段落样式属性
 */
export function updateStyleProperty(
  schema: SchemaInput,
  styleId: string,
  property: string[],
  value: unknown
): SchemaInput {
  const fullPath = ['paragraphStyles', styleId, ...property];
  return immutableSet(schema, fullPath, value);
}

/**
 * 批量更新 Level 属性
 */
export function batchUpdateLevels(
  schema: SchemaInput,
  abstractNumId: number,
  updates: Array<{
    level: number;
    property: string[];
    value: unknown;
  }>,
  options: UpdateOptions = {}
): SchemaInput {
  let result = schema;

  for (const update of updates) {
    // 自动跳过超出 UI 范围的级别
    if (!options.allowHighLevels && update.level > MAX_UI_LEVEL) {
      console.debug(`Skipping update for level ${update.level} (UI limit is ${MAX_UI_LEVEL})`);
      continue;
    }

    result = updateLevelProperty(
      result,
      abstractNumId,
      update.level,
      update.property,
      update.value,
      options
    );
  }

  return result;
}

// ============ 验证 ============

/**
 * 验证 Level 更新
 */
function validateLevelUpdate(property: string[], value: unknown): void {
  const field = property[property.length - 1];

  // 必需字段不能为 null/undefined
  if (REQUIRED_LEVEL_FIELDS.includes(field as any) && value == null) {
    throw new Error(`Required field '${field}' cannot be null or undefined`);
  }

  // 类型验证
  switch (field) {
    case 'start':
      if (typeof value !== 'number' || value < 0) {
        throw new Error(`'start' must be a non-negative number`);
      }
      break;

    case 'left':
    case 'hanging':
      if (typeof value !== 'number' || value < 0) {
        throw new Error(`'${field}' must be a non-negative number (twips)`);
      }
      break;

    case 'level':
      if (typeof value !== 'number' || value < 0 || value > 8) {
        throw new Error(`'level' must be a number between 0 and 8`);
      }
      break;

    case 'numFmt':
      const validFormats = [
        'decimal', 'lowerLetter', 'upperLetter',
        'lowerRoman', 'upperRoman', 'chineseCountingThousand', 'bullet'
      ];
      if (!validFormats.includes(value as string)) {
        throw new Error(`'numFmt' must be one of: ${validFormats.join(', ')}`);
      }
      break;
  }
}

/**
 * 验证整个 Schema 的完整性（用于导出前检查）
 */
export function validateSchema(schema: SchemaInput): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // 验证 numberingSchemas
  for (const [key, abstractNum] of Object.entries(schema.numberingSchemas)) {
    // 检查级别数量
    if (abstractNum.levels.length !== REQUIRED_LEVELS) {
      errors.push(
        `${key}: Expected ${REQUIRED_LEVELS} levels, found ${abstractNum.levels.length}`
      );
    }

    // 检查每个级别
    for (const level of abstractNum.levels) {
      // 必需字段
      if (level.indent.left == null) {
        errors.push(`${key}.levels[${level.level}]: Missing indent.left`);
      }
      if (level.indent.hanging == null) {
        errors.push(`${key}.levels[${level.level}]: Missing indent.hanging`);
      }
      if (!level.numFmt) {
        errors.push(`${key}.levels[${level.level}]: Missing numFmt`);
      }

      // 样式引用检查
      if (level.style && !schema.paragraphStyles[level.style]) {
        warnings.push(
          `${key}.levels[${level.level}]: References style '${level.style}' which doesn't exist`
        );
      }
    }
  }

  // 检查样式继承链
  for (const [styleId, style] of Object.entries(schema.paragraphStyles)) {
    if (style.basedOn && !schema.paragraphStyles[style.basedOn]) {
      warnings.push(
        `Style '${styleId}': basedOn '${style.basedOn}' does not exist`
      );
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

// ============ 样式操作 ============

/**
 * 创建新的段落样式
 */
export function createStyle(
  schema: SchemaInput,
  styleId: string,
  style: ParagraphStyle
): SchemaInput {
  if (schema.paragraphStyles[styleId]) {
    throw new Error(`Style '${styleId}' already exists`);
  }

  return immutableSet(schema, ['paragraphStyles', styleId], style);
}

/**
 * 为 Level 关联样式
 */
export function linkStyleToLevel(
  schema: SchemaInput,
  abstractNumId: number,
  levelIndex: number,
  styleId: string
): SchemaInput {
  // 验证样式存在
  if (!schema.paragraphStyles[styleId]) {
    throw new Error(`Style '${styleId}' does not exist`);
  }

  return updateLevelProperty(
    schema,
    abstractNumId,
    levelIndex,
    ['style'],
    styleId
  );
}

/**
 * 解除 Level 的样式关联
 */
export function unlinkStyleFromLevel(
  schema: SchemaInput,
  abstractNumId: number,
  levelIndex: number
): SchemaInput {
  return updateLevelProperty(
    schema,
    abstractNumId,
    levelIndex,
    ['style'],
    undefined
  );
}

// ============ 查询操作 ============

/**
 * 获取指定 AbstractNum
 */
export function getAbstractNum(
  schema: SchemaInput,
  abstractNumId: number
): AbstractNum | undefined {
  return schema.numberingSchemas[`AbstractNum_${abstractNumId}`];
}

/**
 * 获取指定 Level
 */
export function getLevel(
  schema: SchemaInput,
  abstractNumId: number,
  levelIndex: number
): NumberingLevel | undefined {
  const abstractNum = getAbstractNum(schema, abstractNumId);
  return abstractNum?.levels[levelIndex];
}

/**
 * 获取所有 AbstractNum ID
 */
export function getAbstractNumIds(schema: SchemaInput): number[] {
  const ids: number[] = [];
  for (const key of Object.keys(schema.numberingSchemas)) {
    const match = key.match(/^AbstractNum_(\d+)$/);
    if (match) {
      ids.push(parseInt(match[1], 10));
    }
  }
  return ids.sort((a, b) => a - b);
}

/**
 * 获取所有样式 ID
 */
export function getStyleIds(schema: SchemaInput): string[] {
  return Object.keys(schema.paragraphStyles);
}

// ============ 导入导出 ============

/**
 * 导出 Schema 为 JSON 字符串
 */
export function exportSchema(schema: SchemaInput, pretty = true): string {
  const validation = validateSchema(schema);

  if (!validation.valid) {
    throw new Error(
      `Schema validation failed:\n${validation.errors.join('\n')}`
    );
  }

  if (validation.warnings.length > 0) {
    console.warn('Export warnings:', validation.warnings);
  }

  return JSON.stringify(schema, null, pretty ? 2 : 0);
}

/**
 * 从 JSON 字符串导入 Schema
 */
export function importSchema(json: string): SchemaInput {
  const schema = JSON.parse(json) as SchemaInput;

  // 基础结构验证
  if (!schema.numberingSchemas || typeof schema.numberingSchemas !== 'object') {
    throw new Error('Invalid schema: missing numberingSchemas');
  }
  if (!schema.paragraphStyles || typeof schema.paragraphStyles !== 'object') {
    throw new Error('Invalid schema: missing paragraphStyles');
  }

  return schema;
}

/**
 * 克隆 Schema
 */
export function cloneSchema(schema: SchemaInput): SchemaInput {
  return deepClone(schema);
}

// ============ 变更追踪 ============

/**
 * 比较两个 Schema 的差异
 */
export function compareSchemas(
  before: SchemaInput,
  after: SchemaInput
): ReturnType<typeof computeDiff> {
  return computeDiff(before, after);
}

/**
 * 创建 Schema 快照管理器
 */
export function createSnapshotManager(initialSchema: SchemaInput) {
  const snapshots: SchemaInput[] = [deepClone(initialSchema)];
  let currentIndex = 0;

  return {
    /** 获取当前 Schema */
    getCurrent(): SchemaInput {
      return snapshots[currentIndex];
    },

    /** 保存新快照 */
    save(schema: SchemaInput): void {
      // 移除当前位置之后的所有快照
      snapshots.splice(currentIndex + 1);
      snapshots.push(deepClone(schema));
      currentIndex = snapshots.length - 1;
    },

    /** 撤销 */
    undo(): SchemaInput | null {
      if (currentIndex > 0) {
        currentIndex--;
        return deepClone(snapshots[currentIndex]);
      }
      return null;
    },

    /** 重做 */
    redo(): SchemaInput | null {
      if (currentIndex < snapshots.length - 1) {
        currentIndex++;
        return deepClone(snapshots[currentIndex]);
      }
      return null;
    },

    /** 是否可撤销 */
    canUndo(): boolean {
      return currentIndex > 0;
    },

    /** 是否可重做 */
    canRedo(): boolean {
      return currentIndex < snapshots.length - 1;
    },

    /** 获取快照数量 */
    getSnapshotCount(): number {
      return snapshots.length;
    },
  };
}

// ============ 样式自动生成与关联 ============

/**
 * 为指定级别自动生成并关联样式
 * @param schema - 原始 Schema
 * @param abstractNumId - AbstractNum ID
 * @param level - 级别索引
 * @returns 更新后的 Schema
 */
export function ensureLevelStyle(
  schema: SchemaInput,
  abstractNumId: number,
  level: number
): SchemaInput {
  const styleName = generateStyleName(abstractNumId, level);
  let result = schema;

  // 如果样式不存在，创建它
  if (!result.paragraphStyles[styleName]) {
    const newStyle = generateDefaultStyle(abstractNumId, level);
    result = immutableSet(result, ['paragraphStyles', styleName], newStyle);
  }

  const abstractNum = result.numberingSchemas[`AbstractNum_${abstractNumId}`];
  if (!abstractNum) return result;

  // 如果级别不存在，创建完整的默认级别
  if (!abstractNum.levels[level]) {
    const defaultConfig = generateDefaultLevelConfig(level);
    const newLevel: NumberingLevel = {
      level,
      style: styleName,
      ...defaultConfig,
    };
    result = immutableSet(
      result,
      ['numberingSchemas', `AbstractNum_${abstractNumId}`, 'levels', String(level)],
      newLevel
    );
  } else if (abstractNum.levels[level].style !== styleName) {
    // 级别存在但未关联样式，关联它
    result = immutableSet(
      result,
      ['numberingSchemas', `AbstractNum_${abstractNumId}`, 'levels', String(level), 'style'],
      styleName
    );
  }

  return result;
}

/**
 * 为 AbstractNum 的所有级别自动生成并关联样式
 */
export function ensureAllLevelStyles(
  schema: SchemaInput,
  abstractNumId: number
): SchemaInput {
  let result = schema;
  const abstractNum = result.numberingSchemas[`AbstractNum_${abstractNumId}`];

  if (!abstractNum) {
    throw new Error(`AbstractNum_${abstractNumId} not found`);
  }

  for (let i = 0; i < abstractNum.levels.length; i++) {
    result = ensureLevelStyle(result, abstractNumId, i);
  }

  return result;
}

/**
 * 初始化整个 Schema - 为所有 AbstractNum 的所有级别关联样式，并应用级别继承
 */
export function initializeSchema(schema: SchemaInput): SchemaInput {
  let result = schema;

  // 确保 paragraphStyles 存在
  if (!result.paragraphStyles) {
    result = { ...result, paragraphStyles: {} };
  }

  const ids = getAbstractNumIds(result);

  for (const id of ids) {
    // 确保每个 AbstractNum 都有 REQUIRED_LEVELS (9) 个级别
    const abstractNum = result.numberingSchemas[`AbstractNum_${id}`];
    if (abstractNum && abstractNum.levels.length < REQUIRED_LEVELS) {
      const paddedLevels = [...abstractNum.levels];
      for (let i = abstractNum.levels.length; i < REQUIRED_LEVELS; i++) {
        const defaultConfig = generateDefaultLevelConfig(i);
        paddedLevels.push({
          level: i,
          ...defaultConfig,
        } as NumberingLevel);
      }
      result = immutableSet(
        result,
        ['numberingSchemas', `AbstractNum_${id}`, 'levels'],
        paddedLevels
      );
    }

    // 为所有级别生成并关联样式（只在样式不存在时创建，不会覆盖已有样式）
    result = ensureAllLevelStyles(result, id);
    // 注意：不再自动调用 ensureLevelInheritance，避免覆盖用户的自定义修改
    // 继承逻辑应该只在用户添加新级别时调用，而不是每次加载时都调用
  }

  return result;
}

// ============ 方案管理 ============

/**
 * 获取下一个可用的 AbstractNum ID
 */
export function getNextAbstractNumId(schema: SchemaInput): number {
  const ids = getAbstractNumIds(schema);
  if (ids.length === 0) return 0;
  return Math.max(...ids) + 1;
}

/**
 * 创建新的编号方案
 * @param schema - 原始 Schema
 * @param options - 创建选项
 * @returns 更新后的 Schema 和新方案 ID
 */
export function createAbstractNum(
  schema: SchemaInput,
  options: {
    /** 自定义 ID（可选，默认自动生成） */
    id?: number;
    /** 复制自哪个方案（可选） */
    copyFrom?: number;
    /** 方案名称 */
    name?: string;
  } = {}
): { schema: SchemaInput; id: number } {
  const newId = options.id ?? getNextAbstractNumId(schema);
  const key = `AbstractNum_${newId}`;

  if (schema.numberingSchemas[key]) {
    throw new Error(`AbstractNum_${newId} already exists`);
  }

  let newAbstractNum: AbstractNum;

  if (options.copyFrom !== undefined) {
    // 复制现有方案
    const source = schema.numberingSchemas[`AbstractNum_${options.copyFrom}`];
    if (!source) {
      throw new Error(`Source AbstractNum_${options.copyFrom} not found`);
    }
    newAbstractNum = deepClone(source);
    // 重置样式关联（稍后会重新生成）
    newAbstractNum.levels.forEach(level => {
      level.style = undefined;
    });
  } else {
    // 创建新方案
    const levels: NumberingLevel[] = [];
    for (let i = 0; i < REQUIRED_LEVELS; i++) {
      levels.push({
        level: i,
        ...generateDefaultLevelConfig(i),
        style: undefined,
      });
    }
    newAbstractNum = { abstractNumId: newId, levels };
  }

  // 添加到 schema
  let result = immutableSet(schema, ['numberingSchemas', key], newAbstractNum);

  // 为所有级别生成并关联样式
  result = ensureAllLevelStyles(result, newId);

  // Level 5-8 自动继承 Level 4 的格式
  result = ensureLevelInheritance(result, newId, MAX_UI_LEVEL);

  return { schema: result, id: newId };
}

/**
 * 删除编号方案
 * @param schema - 原始 Schema
 * @param abstractNumId - 要删除的方案 ID
 * @param options - 删除选项
 * @returns 更新后的 Schema
 */
export function deleteAbstractNum(
  schema: SchemaInput,
  abstractNumId: number,
  options: {
    /** 是否同时删除关联的样式 */
    deleteStyles?: boolean;
  } = {}
): SchemaInput {
  const key = `AbstractNum_${abstractNumId}`;
  const abstractNum = schema.numberingSchemas[key];

  if (!abstractNum) {
    throw new Error(`AbstractNum_${abstractNumId} not found`);
  }

  let result = deepClone(schema);

  // 删除关联的样式
  if (options.deleteStyles) {
    for (const level of abstractNum.levels) {
      const styleName = generateStyleName(abstractNumId, level.level);
      if (result.paragraphStyles[styleName]) {
        delete result.paragraphStyles[styleName];
      }
    }
  }

  // 删除方案
  delete result.numberingSchemas[key];

  return result;
}

// ============ 级别管理 ============

/**
 * 确保级别继承：未定义的级别继承最后一个已定义级别的格式
 */
export function ensureLevelInheritance(
  schema: SchemaInput,
  abstractNumId: number,
  definedMaxLevel: number = MAX_UI_LEVEL
): SchemaInput {
  const abstractNum = schema.numberingSchemas[`AbstractNum_${abstractNumId}`];
  if (!abstractNum) {
    throw new Error(`AbstractNum_${abstractNumId} not found`);
  }

  let result = schema;
  const lastDefinedLevel = abstractNum.levels[definedMaxLevel];

  if (!lastDefinedLevel) return result;

  // 获取源级别的样式ID
  const sourceStyleId = lastDefinedLevel.style;
  const sourceStyle = sourceStyleId ? result.paragraphStyles[sourceStyleId] : null;

  // 从源 pattern 中提取后缀（去掉 %n 部分）
  const extractSuffix = (pattern: string): string => {
    if (!pattern) return '';
    return pattern.replace(/%\d/g, '').trim() || '.';
  };

  const sourceSuffix = extractSuffix(lastDefinedLevel.pattern);
  const isBullet = lastDefinedLevel.numFmt === 'bullet';

  // 复制最后一个已定义级别的格式到后续级别
  for (let i = definedMaxLevel + 1; i < REQUIRED_LEVELS; i++) {
    const level = abstractNum.levels[i];
    if (!level) continue;

    // 生成正确的 pattern（对于 bullet 类型直接使用，编号类型需要调整级别引用）
    const newPattern = isBullet
      ? lastDefinedLevel.pattern
      : (lastDefinedLevel.pattern ? `%${i + 1}${sourceSuffix}` : '');

    // 复制格式但调整 pattern 中的级别引用
    const inheritedConfig: Partial<NumberingLevel> = {
      numFmt: lastDefinedLevel.numFmt,
      pattern: newPattern,
      alignment: lastDefinedLevel.alignment,
      start: lastDefinedLevel.start,
    };

    // 更新继承的属性
    for (const [key, value] of Object.entries(inheritedConfig)) {
      result = immutableSet(
        result,
        ['numberingSchemas', `AbstractNum_${abstractNumId}`, 'levels', String(i), key],
        value
      );
    }

    // 调整缩进（每级增加）
    const indentStep = 440;
    result = immutableSet(
      result,
      ['numberingSchemas', `AbstractNum_${abstractNumId}`, 'levels', String(i), 'indent', 'left'],
      lastDefinedLevel.indent.left + (i - definedMaxLevel) * indentStep
    );

    // 确保有样式关联
    result = ensureLevelStyle(result, abstractNumId, i);

    // *** 关键修复：复制源级别的段落样式属性到继承级别的样式 ***
    if (sourceStyle) {
      const targetStyleId = generateStyleName(abstractNumId, i);

      // 复制源样式的所有字体和格式属性
      const stylesToCopy: (keyof ParagraphStyle)[] = [
        'font',
        'fontSize',
        'bold',
        'italic',
        'underline',
        'spacing',
      ];

      for (const prop of stylesToCopy) {
        if (sourceStyle[prop] !== undefined && sourceStyle[prop] !== null) {
          result = immutableSet(
            result,
            ['paragraphStyles', targetStyleId, prop],
            deepClone(sourceStyle[prop])
          );
        }
      }
    }
  }

  return result;
}

/**
 * 复制级别格式
 */
export function copyLevelFormat(
  schema: SchemaInput,
  sourceAbstractNumId: number,
  sourceLevel: number,
  targetAbstractNumId: number,
  targetLevel: number
): SchemaInput {
  const sourceAbstractNum = schema.numberingSchemas[`AbstractNum_${sourceAbstractNumId}`];
  const targetAbstractNum = schema.numberingSchemas[`AbstractNum_${targetAbstractNumId}`];

  if (!sourceAbstractNum || !targetAbstractNum) {
    throw new Error('Source or target AbstractNum not found');
  }

  const source = sourceAbstractNum.levels[sourceLevel];
  if (!source) {
    throw new Error(`Source level ${sourceLevel} not found`);
  }

  let result = schema;

  // 复制基本属性（不包括 level 和 style）
  const propertiesToCopy: (keyof NumberingLevel)[] = [
    'start', 'numFmt', 'pattern', 'indent'
  ];

  for (const prop of propertiesToCopy) {
    if (source[prop] !== undefined) {
      result = immutableSet(
        result,
        ['numberingSchemas', `AbstractNum_${targetAbstractNumId}`, 'levels', String(targetLevel), prop],
        deepClone(source[prop])
      );
    }
  }

  return result;
}

/**
 * 重置级别为默认值
 */
export function resetLevelToDefault(
  schema: SchemaInput,
  abstractNumId: number,
  level: number
): SchemaInput {
  const abstractNum = schema.numberingSchemas[`AbstractNum_${abstractNumId}`];
  if (!abstractNum || !abstractNum.levels[level]) {
    throw new Error(`Level ${level} not found in AbstractNum_${abstractNumId}`);
  }

  const defaultConfig = generateDefaultLevelConfig(level);
  let result = schema;

  // 更新为默认配置
  for (const [key, value] of Object.entries(defaultConfig)) {
    result = immutableSet(
      result,
      ['numberingSchemas', `AbstractNum_${abstractNumId}`, 'levels', String(level), key],
      value
    );
  }

  return result;
}

// ============ 样式管理 ============

/**
 * 更新样式的特定属性
 */
export function updateStyle(
  schema: SchemaInput,
  styleId: string,
  updates: Partial<ParagraphStyle>
): SchemaInput {
  if (!schema.paragraphStyles[styleId]) {
    throw new Error(`Style '${styleId}' not found`);
  }

  let result = schema;
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined) {
      result = immutableSet(result, ['paragraphStyles', styleId, key], value);
    }
  }

  return result;
}

/**
 * 删除样式
 */
export function deleteStyle(
  schema: SchemaInput,
  styleId: string
): SchemaInput {
  if (!schema.paragraphStyles[styleId]) {
    throw new Error(`Style '${styleId}' not found`);
  }

  const result = deepClone(schema);
  delete result.paragraphStyles[styleId];

  // 清除所有引用此样式的级别
  for (const [, abstractNum] of Object.entries(result.numberingSchemas)) {
    for (const level of abstractNum.levels) {
      if (level.style === styleId) {
        level.style = undefined;
      }
    }
  }

  return result;
}

/**
 * 复制样式
 */
export function copyStyle(
  schema: SchemaInput,
  sourceStyleId: string,
  newStyleId: string
): SchemaInput {
  const source = schema.paragraphStyles[sourceStyleId];
  if (!source) {
    throw new Error(`Source style '${sourceStyleId}' not found`);
  }

  if (schema.paragraphStyles[newStyleId]) {
    throw new Error(`Style '${newStyleId}' already exists`);
  }

  const newStyle = deepClone(source);
  newStyle.name = newStyleId;

  return immutableSet(schema, ['paragraphStyles', newStyleId], newStyle);
}
